// A tiny, dependency-free QR code encoder (ISO/IEC 18004) for UPI collect QRs — the kiosk pass sheet (spec/12 §7.1,
// F-KIO-14): byte mode, error correction level M, versions 1–15 (up to 412 bytes — a UPI intent URL is ~150).
// It follows the reference algorithm's shape (Project Nayuki's QR Code generator, MIT): function patterns,
// Reed–Solomon over GF(256), interleaving, zig-zag placement, and the lowest-penalty of the eight masks. The
// kiosk ships it instead of a library to stay inside spec/12 §15's 300 KB lock-screen budget. Pure.
const MAX_VERSION = 15;
// Level M, indexed by version (index 0 unused).
const ECC_PER_BLOCK = [0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24];
const NUM_BLOCKS = [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10];
/** Level M's two format bits. */
const ECL_BITS = 0;
const bit = (value, index) => ((value >>> index) & 1) !== 0;
function rawDataModules(version) {
    let result = (16 * version + 128) * version + 64;
    if (version >= 2) {
        const align = Math.floor(version / 7) + 2;
        result -= (25 * align - 10) * align - 55;
        if (version >= 7)
            result -= 36;
    }
    return result;
}
function dataCodewords(version) {
    return Math.floor(rawDataModules(version) / 8) - ECC_PER_BLOCK[version] * NUM_BLOCKS[version];
}
function alignmentPositions(version, size) {
    if (version === 1)
        return [];
    const count = Math.floor(version / 7) + 2;
    const step = Math.floor((version * 8 + count * 3 + 5) / (count * 4 - 4)) * 2;
    const result = [6];
    for (let pos = size - 7; result.length < count; pos -= step)
        result.splice(1, 0, pos);
    return result;
}
// ---- Reed–Solomon over GF(2^8), modulus 0x11D ----
function gfMultiply(x, y) {
    let z = 0;
    for (let i = 7; i >= 0; i--) {
        z = (z << 1) ^ ((z >>> 7) * 0x11d);
        z ^= ((y >>> i) & 1) * x;
    }
    return z;
}
function rsDivisor(degree) {
    const result = new Array(degree).fill(0);
    result[degree - 1] = 1;
    let root = 1;
    for (let i = 0; i < degree; i++) {
        for (let j = 0; j < degree; j++) {
            result[j] = gfMultiply(result[j], root);
            if (j + 1 < degree)
                result[j] ^= result[j + 1];
        }
        root = gfMultiply(root, 0x02);
    }
    return result;
}
function rsRemainder(data, divisor) {
    const result = divisor.map(() => 0);
    for (const byte of data) {
        const factor = byte ^ result.shift();
        result.push(0);
        divisor.forEach((coefficient, i) => {
            result[i] ^= gfMultiply(coefficient, factor);
        });
    }
    return result;
}
// ---- encoding ----
function utf8(text) {
    return Array.from(new TextEncoder().encode(text));
}
/** The data codewords for `bytes` at `version`: mode, count, data, terminator and padding. */
function dataFor(bytes, version) {
    const bits = [];
    const push = (value, length) => {
        for (let i = length - 1; i >= 0; i--)
            bits.push((value >>> i) & 1);
    };
    const capacity = dataCodewords(version) * 8;
    push(0b0100, 4);
    push(bytes.length, version <= 9 ? 8 : 16);
    for (const byte of bytes)
        push(byte, 8);
    push(0, Math.min(4, capacity - bits.length));
    push(0, (8 - (bits.length % 8)) % 8);
    for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11)
        push(pad, 8);
    const result = [];
    for (let i = 0; i < bits.length; i += 8)
        result.push(parseInt(bits.slice(i, i + 8).join(''), 2));
    return result;
}
/** Split into blocks, add each block's error correction, and interleave. */
function withEcc(data, version) {
    const numBlocks = NUM_BLOCKS[version];
    const eccLen = ECC_PER_BLOCK[version];
    const raw = Math.floor(rawDataModules(version) / 8);
    const numShort = numBlocks - (raw % numBlocks);
    const shortLen = Math.floor(raw / numBlocks);
    const divisor = rsDivisor(eccLen);
    const blocks = [];
    for (let i = 0, k = 0; i < numBlocks; i++) {
        const block = data.slice(k, k + shortLen - eccLen + (i < numShort ? 0 : 1));
        k += block.length;
        const ecc = rsRemainder(block, divisor);
        if (i < numShort)
            block.push(0);
        blocks.push([...block, ...ecc]);
    }
    const result = [];
    for (let i = 0; i < blocks[0].length; i++) {
        blocks.forEach((block, j) => {
            if (i !== shortLen - eccLen || j >= numShort)
                result.push(block[i]);
        });
    }
    return result;
}
/** The 15 format bits (level M, `mask`), BCH-protected and masked, bit 14 first in reading order. */
export function formatBits(mask) {
    const data = (ECL_BITS << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i++)
        rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    return ((data << 10) | rem) ^ 0x5412;
}
/** The 18 version bits (versions ≥ 7). */
export function versionBits(version) {
    let rem = version;
    for (let i = 0; i < 12; i++)
        rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    return (version << 12) | rem;
}
const MASKS = [
    (x, y) => (x + y) % 2 === 0,
    (_x, y) => y % 2 === 0,
    (x) => x % 3 === 0,
    (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
    (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
    (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];
class Matrix {
    modules;
    fixed;
    size;
    constructor(size) {
        this.size = size;
        this.modules = Array.from({ length: size }, () => new Array(size).fill(false));
        this.fixed = Array.from({ length: size }, () => new Array(size).fill(false));
    }
    set(x, y, dark) {
        this.modules[y][x] = dark;
        this.fixed[y][x] = true;
    }
}
function drawFunctionPatterns(matrix, version) {
    const { size } = matrix;
    for (let i = 0; i < size; i++) {
        matrix.set(6, i, i % 2 === 0);
        matrix.set(i, 6, i % 2 === 0);
    }
    for (const [cx, cy] of [
        [3, 3],
        [size - 4, 3],
        [3, size - 4],
    ]) {
        for (let dy = -4; dy <= 4; dy++) {
            for (let dx = -4; dx <= 4; dx++) {
                const x = cx + dx;
                const y = cy + dy;
                const distance = Math.max(Math.abs(dx), Math.abs(dy));
                if (x >= 0 && x < size && y >= 0 && y < size)
                    matrix.set(x, y, distance !== 2 && distance !== 4);
            }
        }
    }
    const positions = alignmentPositions(version, size);
    const last = positions.length - 1;
    positions.forEach((ay, i) => {
        positions.forEach((ax, j) => {
            if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0))
                return;
            for (let dy = -2; dy <= 2; dy++) {
                for (let dx = -2; dx <= 2; dx++)
                    matrix.set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
            }
        });
    });
    drawFormat(matrix, 0); // reserve the format areas; redrawn once the mask is chosen
    if (version >= 7) {
        const bits = versionBits(version);
        for (let i = 0; i < 18; i++) {
            const a = size - 11 + (i % 3);
            const b = Math.floor(i / 3);
            matrix.set(a, b, bit(bits, i));
            matrix.set(b, a, bit(bits, i));
        }
    }
}
function drawFormat(matrix, mask) {
    const { size } = matrix;
    const bits = formatBits(mask);
    for (let i = 0; i <= 5; i++)
        matrix.set(8, i, bit(bits, i));
    matrix.set(8, 7, bit(bits, 6));
    matrix.set(8, 8, bit(bits, 7));
    matrix.set(7, 8, bit(bits, 8));
    for (let i = 9; i < 15; i++)
        matrix.set(14 - i, 8, bit(bits, i));
    for (let i = 0; i < 8; i++)
        matrix.set(size - 1 - i, 8, bit(bits, i));
    for (let i = 8; i < 15; i++)
        matrix.set(8, size - 15 + i, bit(bits, i));
    matrix.set(8, size - 8, true); // the dark module
}
function drawCodewords(matrix, codewords) {
    const { size } = matrix;
    let i = 0;
    for (let right = size - 1; right >= 1; right -= 2) {
        if (right === 6)
            right = 5;
        for (let vertical = 0; vertical < size; vertical++) {
            for (let j = 0; j < 2; j++) {
                const x = right - j;
                const upward = ((right + 1) & 2) === 0;
                const y = upward ? size - 1 - vertical : vertical;
                if (!matrix.fixed[y][x] && i < codewords.length * 8) {
                    matrix.modules[y][x] = bit(codewords[i >>> 3], 7 - (i & 7));
                    i++;
                }
            }
        }
    }
}
function applyMask(matrix, mask) {
    const test = MASKS[mask];
    for (let y = 0; y < matrix.size; y++) {
        for (let x = 0; x < matrix.size; x++) {
            if (!matrix.fixed[y][x] && test(x, y))
                matrix.modules[y][x] = !matrix.modules[y][x];
        }
    }
}
const FINDER_LIKE = ['10111010000', '00001011101'];
/** The standard mask penalty (runs, 2×2 blocks, finder-like patterns, dark balance). Lower is better. */
function penalty(modules) {
    const size = modules.length;
    let score = 0;
    let dark = 0;
    const lines = [];
    for (let y = 0; y < size; y++) {
        let row = '';
        let column = '';
        for (let x = 0; x < size; x++) {
            row += modules[y][x] ? '1' : '0';
            column += modules[x][y] ? '1' : '0';
            if (modules[y][x])
                dark++;
            if (x < size - 1 && y < size - 1) {
                const c = modules[y][x];
                if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1])
                    score += 3;
            }
        }
        lines.push(row, column);
    }
    for (const line of lines) {
        for (const run of line.match(/0{5,}|1{5,}/g) ?? [])
            score += run.length - 2;
        for (const pattern of FINDER_LIKE) {
            for (let at = line.indexOf(pattern); at !== -1; at = line.indexOf(pattern, at + 1))
                score += 40;
        }
    }
    const total = size * size;
    return score + Math.floor(Math.abs(dark * 20 - total * 10) / total) * 10;
}
/** Encode `text` (UTF-8, byte mode, level M). Throws when it does not fit version 15. */
export function encodeQr(text) {
    const bytes = utf8(text);
    let version = 1;
    while (4 + (version <= 9 ? 8 : 16) + bytes.length * 8 > dataCodewords(version) * 8) {
        version++;
        if (version > MAX_VERSION)
            throw new RangeError('QR payload too long');
    }
    const size = version * 4 + 17;
    const codewords = withEcc(dataFor(bytes, version), version);
    let best = null;
    let bestScore = Infinity;
    let bestMask = 0;
    for (let mask = 0; mask < 8; mask++) {
        const matrix = new Matrix(size);
        drawFunctionPatterns(matrix, version);
        drawCodewords(matrix, codewords);
        applyMask(matrix, mask);
        drawFormat(matrix, mask);
        const score = penalty(matrix.modules);
        if (score < bestScore) {
            best = matrix;
            bestScore = score;
            bestMask = mask;
        }
    }
    return { size, version, mask: bestMask, modules: best.modules };
}
/** The dark modules as one SVG path (`M x y h1v1h-1z` per module), for a crisp, tiny render. */
export function qrPath(code) {
    let path = '';
    code.modules.forEach((row, y) => row.forEach((dark, x) => {
        if (dark)
            path += `M${x} ${y}h1v1h-1z`;
    }));
    return path;
}
//# sourceMappingURL=qr.js.map